import { Box, FormControl, Grid, MenuItem, Select, SelectChangeEvent, Theme, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DesktopTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { format } from 'date-fns';
import { get as _get } from 'lodash';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { converseNext, createDraftClaim } from '../../../api/converseApi';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import { saveDateLocation, updateDraftPayload } from '../../../app/store/ClaimCreation/ClaimCreationSlice';
import { addDraftClaim } from '../../../app/store/Claims/ClaimsSlice';
import {
  getAuthKey,
  getClaimCreationProductType,
  getMobileSourceSystem,
  getQuestionSetPartner,
  getSessionId,
  getUrlKey,
  getUserData,
  userIsAdjuster,
  userIsThirdParty,
} from '../../../app/store/combinedSelectors';
import { updateAuthToken } from '../../../app/store/ContextRouter/ContextRouterActions';
import {
  backBtn,
  cancelBtn,
  continueBtn,
  FNOLCancelConfirmationConstants,
  GenericModalTopMargin,
} from '../../../Constants/Constants';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import {
  buildDraftClaimPayload,
  createInitiateDynamicQuestionPayload,
  mapLocalDraftToCCFormat,
} from '../../../services/SetupFnol.service';
import { GenericProductType } from '../../../types/type';
import { getPolicySource } from '../../../utils/PolicySource-Util';
import { CITY_REGEX } from '../../../utils/Regex-Util';
import { postMessageToParentApp, usStates } from '../../../utils/Utilities';
import CallToAction from '../../shared/CallToAction/CallToAction';
import ErrorBanner from '../../shared/ErrorBanner/ErrorBanner';
import GenericModal from '../../shared/GenericModal/GenericModal';
import ModalLinearActivityIndicator from '../../shared/ModalLinearActivityIndicator/ModalLinearActivityIndicator';
import { RegexTextField } from '../../shared/RegexTextField/RegexTextField';

export default function TimeLocationSelection() {
  const theme: Theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const opco = useSelector(getQuestionSetPartner);
  const isThirdParty = useSelector(userIsThirdParty);
  const isAdjuster = useSelector(userIsAdjuster);
  const customerData = useSelector(getUserData);
  const claimsData = useAppSelector((state) => state.claims);
  const fnolData = useAppSelector((state) => state.claimCreation);
  const typeOfClaim = useSelector(getClaimCreationProductType);
  const urlKey = useSelector(getUrlKey);
  const sessionId = useSelector(getSessionId);
  const authKey = useSelector(getAuthKey);
  const mobileSourceSystem = useSelector(getMobileSourceSystem);
  const policies = useAppSelector((state) => state.policies.policies);

  const [date, setDate] = React.useState<any>(new Date());
  const [time, setTime] = React.useState<any | null>('');
  const [city, setCity] = React.useState<string>('');
  const [state, setState] = React.useState<string>('');
  const isAuto = typeOfClaim === GenericProductType.Auto;

  const [showErrorBanner, setShowErrorBanner] = React.useState(false);
  const [modalLoadingOpen, setModalLoadingOpen] = React.useState(false);
  const [cancelConfirmationModalOpen, setCancelConfirmationModalOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const vehicleIsOnPolicy = fnolData.riskInfo.vehicleTypeName !== 'Vehicle is not on policy';

  const [dateTouched, setDateTouched] = React.useState(false);
  const [timeTouched, setTimeTouched] = React.useState(false);

  // checking if the selected date today
  const isToday = date ? new Date().toDateString() === new Date(date).toDateString() : false;

  // Combine date and time into one Date object
  function getCombinedDateTime(date: Date, time: any) {
    if (!(date && time instanceof Date && !isNaN(time.getTime()))) return null;
    const combined = new Date(date);
    combined.setHours(time.getHours());
    combined.setMinutes(time.getMinutes());
    combined.setSeconds(time.getSeconds());
    combined.setMilliseconds(time.getMilliseconds());
    return combined;
  }

  // --- Date Validation ---
  let dateErrorText = '';
  let isDateError = false;

  if (dateTouched) {
    if (!date) {
      dateErrorText = 'Date is required';
      isDateError = true;
    } else if (!(date instanceof Date) || isNaN(date.getTime())) {
      dateErrorText = 'Date format is invalid';
      isDateError = true;
    } else if (date > new Date()) {
      dateErrorText = 'Date cannot be in the future';
      isDateError = true;
    }
  }

  // --- Time Validation ---
  let timeErrorText = '';
  let isTimeError = false;

  if (timeTouched) {
    if (!time) {
      timeErrorText = 'Time is required';
      isTimeError = true;
    } else if (!(time instanceof Date) || isNaN(time.getTime())) {
      timeErrorText = 'Time format is invalid';
      isTimeError = true;
    } else if (isToday) {
      const combined = getCombinedDateTime(date, time);
      if (combined && combined > new Date()) {
        timeErrorText = 'Time cannot be in the future';
        isTimeError = true;
      }
    }
  }

  function handleCityChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const newCity: string = _get(event, 'target.value', '');
    setCity(newCity);
  }

  function handleStateChange(event: SelectChangeEvent) {
    const newState: string = _get(event, 'target.value', '');
    setState(newState);
  }

  async function handleNext() {
    setShowErrorBanner(false);
    setModalLoadingOpen(true);
    let formattedLossDate: any;
    if (date && time) {
      // Create a new Date object from 'date'
      const combined = new Date(date);
      // Set the time from the 'time' object
      combined.setHours(time.getHours());
      combined.setMinutes(time.getMinutes());
      combined.setSeconds(time.getSeconds());
      combined.setMilliseconds(time.getMilliseconds());
      // Format the combined date and time
      formattedLossDate = format(combined, 'yyyy-MM-dd HH:mm:ss');
    }

    const dateTime = {
      lossDate: formattedLossDate,
      city: city,
      state: state,
    };

    const dateToCheck = format(new Date(date), 'MM/dd/yyyy');

    dispatch(saveDateLocation({ dateLocationInfo: dateTime }));
    const matchingDrafts = _get(claimsData, 'draftClaims', []).filter((draft: any) => {
      if (isAuto) {
        if (vehicleIsOnPolicy) {
          return (
            !!_get(draft, 'vehicleDetail', []).find((detail: any) => detail.VIN === fnolData.riskInfo.vin) &&
            draft.lossDate === dateToCheck
          );
        }
        // When vehicle is not on policy, we will just check lossDate vs dateToCheck
        else {
          return draft.lossDate === dateToCheck;
        }
      } else {
        return (
          draft.lossLocationAddress?.addressLine1 === fnolData.riskInfo.address.addressLine1 &&
          draft.lossDate === dateToCheck
        );
      }
    });

    const matchingOpenClaims = _get(claimsData, 'openClaims', [])
      .filter((openClaim: any) => {
        if (isAuto) {
          if (vehicleIsOnPolicy) {
            return (
              !!_get(openClaim, 'vehicleDetail', []).find((detail: any) => detail.VIN === fnolData.riskInfo.vin) &&
              openClaim.lossDate === dateToCheck
            );
          }
          // When vehicle is not on policy, we will just check lossDate vs dateToCheck
          else {
            return openClaim.lossDate === dateToCheck;
          }
        } else {
          return (
            openClaim.lossLocationAddress?.addressLine1 === fnolData.riskInfo.address.addressLine1 &&
            openClaim.lossDate === dateToCheck
          );
        }
      })
      .map((claim: any) => {
        return {
          ...claim,
          isFullClaim: true,
        };
      });

    const matchingClosedClaims = _get(claimsData, 'closedClaims', [])
      .filter((closedClaim: any) => {
        if (isAuto) {
          return (
            !!_get(closedClaim, 'vehicleDetail', []).find((detail: any) => detail.VIN === fnolData.riskInfo.vin) &&
            closedClaim.lossDate === dateToCheck
          );
        } else {
          return (
            closedClaim.lossLocationAddress?.addressLine1 === fnolData.riskInfo.address.addressLine1 &&
            closedClaim.lossDate === dateToCheck
          );
        }
      })
      .map((claim: any) => {
        return {
          ...claim,
          isFullClaim: true,
        };
      });

    if (matchingDrafts.length > 0 || matchingOpenClaims.length > 0 || matchingClosedClaims.length > 0) {
      const draftsAndFull =
        matchingDrafts.length > 0 || matchingOpenClaims.length > 0 || matchingClosedClaims.length > 0;
      navigate('/duplicate-check', {
        state: {
          dupClaims: matchingDrafts.concat(matchingOpenClaims).concat(matchingClosedClaims),
          draftsAndFull: draftsAndFull,
        },
      });
    } else {
      const draftClaimBody = buildDraftClaimPayload(customerData, fnolData, dateTime, opco, mobileSourceSystem, {
        creatingDraft: true,
      });

      // Calculate headers once to ensure consistency between createDraftClaim and converseNext calls
      const lob = isAuto ? 'auto' : 'property';
      let policySource = '';
      if (isAuto) {
        // Get Policy Number from fnolData
        const policyNumber = fnolData.riskInfo.policyNumber;
        // Get Policy Source
        policySource = getPolicySource(policyNumber, policies);
      }

      // Pass headers to createDraftClaim so API can use them for its converseNext call
      const response = await createDraftClaim(
        urlKey,
        sessionId,
        authKey,
        draftClaimBody,
        lob, // line-of-business header
        opco, // partner-id header
        policySource // policy-source header
      );

      if (response.claimNumber && response.claimNumber.length > 0) {
        const converseDraftClaim = buildDraftClaimPayload(customerData, fnolData, dateTime, opco, mobileSourceSystem, {
          creatingDraft: false,
        });

        const converseBody = createInitiateDynamicQuestionPayload(response.claimNumber, converseDraftClaim);

        // Check if the draft claim creation API returned converse data
        const converseData = _get(response, 'converseData', null);
        const converseError = _get(response, 'converseError', null);

        let converseResponse;

        if (converseData && _get(converseData, 'status.code', 0) >= 200) {
          // Happy path: Use converseData from API response
          converseResponse = {
            data: converseData,
            status: { statusCode: converseData.status.code },
          };
          setModalLoadingOpen(false);
        } else if (converseError) {
          // Fallback: Make converseNext call from UI
          converseResponse = await converseNext(
            urlKey,
            sessionId,
            authKey,
            lob,
            opco,
            converseBody.draftClaimNumber,
            converseBody.payload,
            policySource
          );
          setModalLoadingOpen(false);
        } else {
          // Legacy fallback
          converseResponse = await converseNext(
            urlKey,
            sessionId,
            authKey,
            lob,
            opco,
            converseBody.draftClaimNumber,
            converseBody.payload,
            policySource
          );
          setModalLoadingOpen(false);
        }

        if (
          _get(converseResponse, 'status.statusCode', '') >= 200 ||
          _get(converseResponse, 'data.status.code', '') >= 200
        ) {
          dispatch(updateAuthToken(converseResponse.data.updatedAuthToken));
          const ccDraftClaim = mapLocalDraftToCCFormat(draftClaimBody, converseBody.draftClaimNumber);
          dispatch(addDraftClaim({ draft: ccDraftClaim }));
          dispatch(updateDraftPayload({ draftPayload: converseDraftClaim }));
          navigate('/dynamic-question', {
            state: {
              activeQuestion: converseResponse.data.response,
              draftClaimNumber: response.claimNumber,
              isAuto: isAuto,
            },
          });
        } else {
          setModalLoadingOpen(false);
          postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_DRAFT_CLAIM_CREATE_ERROR, '', converseResponse);
          setShowErrorBanner(true);
        }
      } else if (isThirdParty) {
        navigate('/third-party-dup-claim');
      } else {
        // draft claim creation failure
        setModalLoadingOpen(false);
        postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_DRAFT_CLAIM_CREATE_ERROR, '', response);
        setShowErrorBanner(true);
      }
    }
  }

  function handleBack() {
    if (isAuto) {
      navigate('/driver-selection');
    } else if (isAdjuster) {
      navigate('/confirm-insureds');
    } else {
      navigate('/risk-selection');
    }
  }

  function handleConfirmedCancel() {
    navigate('/claims-overview');
  }

  function handleCancel() {
    if (isMobile && theme.custom.styles.showModalPopUpOnFNOLCancel) {
      postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_SCROLL);
      setCancelConfirmationModalOpen(true);
    } else {
      if (isThirdParty) {
        navigate('/third-party-guest-type');
      } else {
        handleConfirmedCancel();
      }
    }
  }

  function handleCloseButtonClick() {
    setCancelConfirmationModalOpen(false);
  }

  return (
    <Box sx={{ width: '100%' }}>
      {!!cancelConfirmationModalOpen && (
        <GenericModal
          title={FNOLCancelConfirmationConstants.TITLE}
          description={FNOLCancelConfirmationConstants.DESCRIPTION}
          primaryButtonText={FNOLCancelConfirmationConstants.PRIMARY_BUTTON_TEXT}
          secondaryButtonText={FNOLCancelConfirmationConstants.SECONDARY_BUTTON_TEXT}
          alertBoxStyle={theme.custom.styles.genericModalCancelAlertBoxStyle}
          verticalPositionForMobile={GenericModalTopMargin}
          theme={theme}
          closeButtonActionHandler={handleCloseButtonClick}
          primaryButtonActionHandler={handleConfirmedCancel}
          secondaryButtonActionHandler={handleCloseButtonClick}
        ></GenericModal>
      )}
      {showErrorBanner && <ErrorBanner></ErrorBanner>}
      {modalLoadingOpen && <ModalLinearActivityIndicator />}
      <Grid
        container
        spacing={isMobile ? 0 : 2}
        sx={{
          width: '100%',
          marginTop: '1em',
        }}
      >
        <Grid item xs={12}>
          <Typography
            sx={{
              ...theme.custom.styles.titleType4,
            }}
          >
            When did this happen?
          </Typography>
        </Grid>

        <Grid sx={{ marginTop: '1rem' }} item xs={12} sm={6}>
          <Typography sx={theme.custom.styles.formLabelStyle}>Date of Incident</Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DesktopDatePicker
              value={date}
              onChange={(newDate) => setDate(newDate)}
              // Adding custom options to the 'Date of Incident' element
              // error: hard setting to false to avoid highlighting
              slotProps={{
                textField: {
                  size: 'small',
                  error: false,
                  helperText: isDateError ? (
                    <Typography sx={theme.custom.styles.errorSubcaseFont}>{dateErrorText}</Typography>
                  ) : (
                    ''
                  ),
                  fullWidth: true,
                  onBlur: () => setDateTouched(true),
                },
              }}
              maxDate={new Date()}
            />
          </LocalizationProvider>
        </Grid>
        <Grid sx={{ marginTop: '1rem' }} item xs={12} sm={6}>
          <Typography sx={theme.custom.styles.formLabelStyle}>Time of Incident</Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ width: '100%' }}>
              <DesktopTimePicker
                value={time}
                onChange={(newTime) => {
                  setTime(newTime);
                  setTimeTouched(true);
                }}
                timeSteps={{ minutes: 1 }}
                // Adding custom options to the 'Time of Incident' element
                // error: hard setting to false to avoid highlighting
                slotProps={{
                  layout: {
                    sx: {
                      '& .MuiMultiSectionDigitalClockSection-root:after': {
                        display: 'none',
                      },
                    },
                  },
                  textField: {
                    size: 'small',
                    error: false,
                    helperText: isTimeError ? (
                      <Typography sx={theme.custom.styles.errorSubcaseFont}>{timeErrorText}</Typography>
                    ) : (
                      ''
                    ),
                    fullWidth: true,
                    onBlur: () => setTimeTouched(true),
                  },
                }}
              />
            </Box>
          </LocalizationProvider>
        </Grid>
      </Grid>

      {isAuto && (
        <Grid
          container
          spacing={isMobile ? 0 : 2}
          sx={{
            width: '100%',
            marginTop: '1em',
          }}
        >
          <Grid item xs={12}>
            <Typography
              sx={{
                ...theme.custom.styles.titleType4,
              }}
            >
              Where did this happen?
            </Typography>
          </Grid>
          <Grid sx={{ marginTop: '0em' }} item xs={12} sm={6}>
            <Typography sx={theme.custom.styles.formLabelStyle}>City</Typography>
            <RegexTextField
              id="city"
              value={city}
              InputLabelProps={{
                shrink: true,
              }}
              regex={CITY_REGEX}
              changeHandler={handleCityChange}
              sx={{ width: '100%' }}
              variant="outlined"
            />
          </Grid>

          <Grid sx={{ marginTop: '0em' }} item xs={12} sm={6}>
            <Typography sx={theme.custom.styles.formLabelStyle}>State</Typography>
            <FormControl fullWidth size="small">
              <Select
                onChange={handleStateChange}
                value={state}
                sx={{
                  '.MuiOutlinedInput-notchedOutline': {
                    borderWidth: theme.custom.styles.defaultBorderWidth,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: theme.custom.styles.defaultBorderWidth,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderWidth: theme.custom.styles.defaultBorderWidth,
                  },
                }}
              >
                {usStates &&
                  usStates.map((state: { value: string; name: string }) => (
                    <MenuItem value={state.value} key={state.value}>
                      {state.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      )}
      <Grid item xs={12}>
        <CallToAction
          nextButton={true}
          nextButtonDisabled={isDateError || isTimeError || !date || !time || (isAuto && (!state || !city))}
          nextButtonContent={continueBtn}
          handleNext={handleNext}
          backButton={!isThirdParty}
          backButtonContent={backBtn}
          handleBack={handleBack}
          skipButton={false}
          cancelButton={true}
          cancelButtonContent={cancelBtn}
          handleCancel={handleCancel}
        ></CallToAction>
      </Grid>
    </Box>
  );
}
